/**
 * Conversation Service
 * Handles LLM interactions for blueprint creation and editing
 */

import {
  validateBlueprint,
  calculateCompletionScore,
  createDefaultBlueprint
} from '@/types/blueprint'
import { app } from '@/firebase/init'

/**
 * Get the Functions URL dynamically (supports emulators/production)
 */
function getFunctionsUrl(endpoint) {
  const region = 'us-central1';
  const projectId = app.options.projectId;
  return `https://${region}-${projectId}.cloudfunctions.net/${endpoint}`;
}

/**
 * Send a message to the LLM for blueprint conversation
 * Uses the existing invokeLLM Firebase Function endpoint
 */
export async function sendConversationMessage(message, blueprint = null, conversationHistory = []) {
  try {
    const payload = {
      provider: 'google', // Use Google Gemini as default
      tier: 'fast',
      stream: false, // We'll handle streaming separately if needed
      prompt: message,
      history: conversationHistory,
      system: getSystemPrompt(blueprint)
    }

    console.log('Sending to LLM:', getFunctionsUrl('invokeLLM'))

    // Use invokeLLM endpoint for raw LLM access (experimental)
    const response = await fetch(getFunctionsUrl('invokeLLM'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add auth headers if needed
      },
      body: JSON.stringify({
        provider: 'google', // Use Google Gemini for experimental testing
        tier: 'fast',
        stream: false,
        prompt: message,
        history: conversationHistory,
        system: getSystemPrompt(blueprint)
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('LLM request failed:', response.status, errorText)
      throw new Error(`LLM request failed: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const data = await response.json()
    console.log('LLM response:', data)

    // Handle invokeLLM response format
    return {
      success: true,
      response: data.content || data.message || 'Response received',
      tokens: data.tokens || 0
    }
  } catch (error) {
    console.error('Conversation service error:', error)

    // If it's a CORS error, provide helpful guidance
    if (error.message.includes('CORS') || error.message.includes('ERR_FAILED')) {
      return {
        success: false,
        error: 'CORS_ERROR',
        response: 'There\'s a connection issue with the AI service. Please make sure the Firebase Functions emulator is running correctly with: `firebase emulators:start --only functions` and that you have the necessary API keys configured.'
      }
    }

    // If it's a connection error, provide helpful guidance
    if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('Failed to fetch')) {
      return {
        success: false,
        error: 'CONNECTION_ERROR',
        response: 'I\'m having trouble connecting to the game designer AI. Please make sure the Firebase Functions emulator is running with: `firebase emulators:start --only functions`'
      }
    }

    return {
      success: false,
      error: error.message,
      response: 'Sorry, I encountered an error. Please try again.'
    }
  }
}

/**
 * Generate system prompt for blueprint conversations
 */
function getSystemPrompt(blueprint = null) {
  const currentBlueprint = blueprint || createDefaultBlueprint()

  return `You are a game design assistant helping developers test a blueprint-based game creation system.

Current Blueprint Status:
${JSON.stringify(currentBlueprint, null, 2)}

Your role:
1. Help fill in missing blueprint sections through conversation
2. Ask one specific question at a time about missing elements
3. Suggest blueprint updates when you have enough information
4. Keep responses focused and technical for testing purposes
5. Structure responses to either ask questions or propose updates

Blueprint Sections to Complete:
- Meta: title, author, kernel (platformer|topdown|card|puzzle|tactics), mode (prototype|full)
- Objectives: what the player needs to achieve
- Controls: how the player interacts (keyboard|touch)
- Rules: game mechanics and logic (when X happens, do Y)
- Levels: game world structure and progression
- Assets: visual/audio themes and packs
- Progression: how levels connect and unlock

Response Format:
- Ask specific questions about missing sections
- Propose blueprint updates with clear reasoning
- Keep responses concise and focused for testing`
}

/**
 * Classify user intent for blueprint operations
 */
export function classifyIntent(message) {
  const lowerMessage = message.toLowerCase()

  // Check for creation intents
  if (lowerMessage.match(/\b(make|create|build|design)\b.*\b(game|world|adventure)\b/)) {
    return {
      intent: 'CREATE_GAME',
      confidence: 0.9,
      target: null
    }
  }

  // Check for edit intents
  if (lowerMessage.match(/\b(add|remove|change|modify|edit|tweak|update)\b/)) {
    return {
      intent: 'EDIT_GAME',
      confidence: 0.8,
      target: extractEditTarget(message)
    }
  }

  // Check for asset-related intents
  if (lowerMessage.match(/\b(asset|sprite|sound|music|image|model)\b/)) {
    return {
      intent: 'MANAGE_ASSETS',
      confidence: 0.7,
      target: 'assets'
    }
  }

  // Check for play/test intents
  if (lowerMessage.match(/\b(play|test|try|run|preview)\b/)) {
    return {
      intent: 'PLAY_GAME',
      confidence: 0.8,
      target: null
    }
  }

  // Check for help intents
  if (lowerMessage.match(/\b(help|how|what|explain)\b/)) {
    return {
      intent: 'GET_HELP',
      confidence: 0.7,
      target: null
    }
  }

  return {
    intent: 'UNKNOWN',
    confidence: 0.3,
    target: null
  }
}

/**
 * Extract edit target from user message
 */
function extractEditTarget(message) {
  const lowerMessage = message.toLowerCase()

  if (lowerMessage.includes('level') || lowerMessage.includes('world')) return 'levels'
  if (lowerMessage.includes('rule') || lowerMessage.includes('mechanic')) return 'rules'
  if (lowerMessage.includes('objective') || lowerMessage.includes('goal')) return 'objectives'
  if (lowerMessage.includes('control') || lowerMessage.includes('input')) return 'controls'
  if (lowerMessage.includes('asset') || lowerMessage.includes('sprite') || lowerMessage.includes('sound')) return 'assets'
  if (lowerMessage.includes('progression') || lowerMessage.includes('unlock')) return 'progression'

  return 'general'
}

/**
 * Generate conversation context for LLM
 */
export function generateConversationContext(blueprint, conversationHistory = []) {
  const completion = calculateCompletionScore(blueprint)
  const validation = validateBlueprint(blueprint)

  return {
    blueprint,
    completion,
    validation,
    history: conversationHistory.slice(-10), // Last 10 messages
    context: {
      missingSections: completion.completed,
      nextRecommendedSection: getNextRecommendedSection(blueprint),
      currentKernel: blueprint.meta.kernel,
      gameTitle: blueprint.meta.title || 'Untitled Game'
    }
  }
}

/**
 * Determine next recommended section to fill
 */
function getNextRecommendedSection(blueprint) {
  const sections = ['meta', 'objectives', 'controls', 'rules', 'levels', 'assets']

  for (const section of sections) {
    if (section === 'meta' && (!blueprint.meta.title || !blueprint.meta.kernel)) {
      return 'meta'
    }
    if (section === 'objectives' && (!blueprint.objectives || blueprint.objectives.length === 0)) {
      return 'objectives'
    }
    if (section === 'controls' && (!blueprint.controls || !blueprint.controls.scheme)) {
      return 'controls'
    }
    if (section === 'rules' && (!blueprint.rules || blueprint.rules.length === 0)) {
      return 'rules'
    }
    if (section === 'levels' && (!blueprint.levels || blueprint.levels.length === 0)) {
      return 'levels'
    }
    if (section === 'assets' && (!blueprint.assets || !blueprint.assets.packs || blueprint.assets.packs.length === 0)) {
      return 'assets'
    }
  }

  return 'progression' // All basic sections complete
}

/**
 * Process LLM response and extract blueprint updates
 */
export function processLLMResponse(response, currentBlueprint) {
  try {
    // This is a simplified version - in a real implementation,
    // you'd use more sophisticated NLP to extract structured updates
    const suggestions = extractBlueprintSuggestions(response)

    if (suggestions.length > 0) {
      return {
        success: true,
        updates: suggestions,
        response: response
      }
    }

    return {
      success: true,
      updates: [],
      response: response
    }
  } catch (error) {
    console.error('Error processing LLM response:', error)
    return {
      success: false,
      error: error.message,
      response: response
    }
  }
}

/**
 * Extract blueprint suggestions from LLM response
 * This is a placeholder - in practice you'd use more sophisticated parsing
 */
function extractBlueprintSuggestions(response) {
  const suggestions = []

  // Simple keyword-based extraction (this would be more sophisticated in reality)
  if (response.toLowerCase().includes('objective') && response.toLowerCase().includes('collect')) {
    suggestions.push({
      section: 'objectives',
      action: 'add',
      value: 'Collect items to win'
    })
  }

  if (response.toLowerCase().includes('control') && response.toLowerCase().includes('jump')) {
    suggestions.push({
      section: 'controls',
      action: 'update',
      value: { scheme: 'keyboard', map: { jump: 'SPACE' } }
    })
  }

  return suggestions
}

/**
 * Apply blueprint updates from conversation
 */
export function applyBlueprintUpdates(blueprint, updates) {
  const updatedBlueprint = JSON.parse(JSON.stringify(blueprint))

  updates.forEach(update => {
    switch (update.section) {
      case 'objectives':
        if (update.action === 'add' && !updatedBlueprint.objectives.includes(update.value)) {
          updatedBlueprint.objectives.push(update.value)
        }
        break

      case 'controls':
        if (update.action === 'update') {
          Object.assign(updatedBlueprint.controls, update.value)
        }
        break

      case 'meta':
        if (update.action === 'update') {
          Object.assign(updatedBlueprint.meta, update.value)
        }
        break

      // Add more cases for other sections
    }
  })

  return updatedBlueprint
}

/**
 * Generate conversation suggestions for next steps
 */
export function generateConversationSuggestions(blueprint) {
  const suggestions = []
  const completion = calculateCompletionScore(blueprint)

  if (completion.percentage < 100) {
    const nextSection = getNextRecommendedSection(blueprint)
    suggestions.push(`Let's work on the ${nextSection} section next.`)
  }

  if (blueprint.rules.length === 0) {
    suggestions.push('We should add some game rules to make it fun!')
  }

  if (blueprint.levels.length === 0) {
    suggestions.push('How about we design the first level?')
  }

  return suggestions
}
